import { useRef, useEffect, useState, useMemo } from 'react';
import { Bell, AlertCircle, CheckCircle2, XCircle, ChevronDown, ChevronRight, MessageCircle } from 'lucide-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { notificationsApi, type NotificationDto } from '@/features/notifications.api';
import { useAuthStore } from '@/stores/auth.store';
import { useToastStore } from '@/stores/toast.store';
import { useAudioStore } from '@/stores/audio.store';
import { useChatUi } from '@/features/chat/chatStore';
import { useT, type StringKey } from '@/i18n';
import { cn } from '@/lib/utils';

const SEEN_STORAGE_KEY = 'pathway.notifBell.lastSeenId';
const POLL_INTERVAL_MS = 30_000;

// F5-R1a — severity / status pill colors, tokens del brand (antes catalog E
// pastels): info=teal · aviso=amber · alerta real=red · positivo=sage.
const SEVERITY_TONE: Record<string, { bg: string; text: string }> = {
  low: { bg: 'bg-nav-hair', text: 'text-nav-slate' },
  medium: { bg: 'bg-nav-tealTint', text: 'text-nav-tealDark' },
  high: { bg: 'bg-nav-amberTint', text: 'text-nav-amberText' },
  blocking: { bg: 'bg-nav-redTint', text: 'text-nav-red' },
};

const STATUS_TONE: Record<string, { bg: string; text: string }> = {
  resolved: { bg: 'bg-nav-sageTint', text: 'text-nav-sageText' },
  wontfix: { bg: 'bg-nav-tealTint', text: 'text-nav-tealDark' },
};

interface BugResolvedPayload {
  bugReportId?: string;
  status?: 'resolved' | 'wontfix';
  resolutionNote?: string | null;
  resolvedBy?: string | null;
  resolvedByName?: string | null;
  resolvedAt?: string | null;
  originalMessage?: string;
  originalSeverity?: string;
}

interface MissedBookingPayload {
  patientLastName?: string;
  patientMrnLast6?: string;
  agentName?: string;
}

interface ChatMessagePayload {
  threadId?: string;
  messageId?: string;
  senderId?: string;
  senderName?: string;
  preview?: string;
  threadName?: string;
}


interface UploaderFlagAdminPayload {
  screeningId?: string;
  clientLastName?: string;
  clientCinLast6?: string;
  agentName?: string;
  error?: string;
  uploaderName?: string;
}

interface UploaderFlagAgentPayload {
  screeningId?: string;
  clientLastName?: string;
  clientCinLast6?: string;
  error?: string;
  uploaderName?: string;
}

interface SlaBreachPayload {
  screeningId?: string;
  clientLastName?: string;
  clientCinLast6?: string;
  agentName?: string;
  hoursSinceSubmit?: number;
}

export function NotificationsBell(): JSX.Element {
  const t = useT();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const userRole = useAuthStore((s) => s.user?.role);
  const audioUnlocked = useAudioStore((s) => s.audioUnlocked);
  const pushToast = useToastStore((s) => s.push);
  const setChatOpen = useChatUi((st) => st.setOpen);
  const selectThread = useChatUi((st) => st.selectThread);
  const [isOpen, setIsOpen] = useState(false);
  const [expandedIds, setExpandedIds] = useState<Record<string, boolean>>({});
  const containerRef = useRef<HTMLDivElement>(null);

  // Track last-seen id for toast firing. Initialised on first poll (no toasts on initial load).
  const lastSeenIdRef = useRef<number | null>(null);
  const initialisedRef = useRef<boolean>(false);

  // Restore lastSeenId from sessionStorage if present (survives in-tab navigation).
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(SEEN_STORAGE_KEY);
      if (raw) {
        const n = parseInt(raw, 10);
        if (Number.isFinite(n) && n > 0) {
          lastSeenIdRef.current = n;
          initialisedRef.current = true;
        }
      }
    } catch {
      // sessionStorage may be unavailable (private mode etc.) — degrade silently.
    }
  }, []);

  // Query for unread count
  const { data: countData } = useQuery({
    queryKey: ['notifications', 'unread-count'],
    queryFn: () => notificationsApi.getUnreadCount(),
    refetchInterval: 60_000,
  });

  // Always-on poll for the notification list (so we can fire toasts on new arrivals).
  // Pull a generous slice so we don't miss anything between polls.
  const { data: notifications = [] } = useQuery<NotificationDto[]>({
    queryKey: ['notifications', 'list'],
    queryFn: () => notificationsApi.getNotifications(20, false),
    refetchInterval: POLL_INTERVAL_MS,
  });

  const unreadCount = countData?.count ?? 0;

  // Fire toasts for newly-arrived notifications.
  useEffect(() => {
    if (!notifications || notifications.length === 0) return;

    // notifications are most-recent-first. Find max id.
    const maxId = notifications.reduce((m, n) => {
      const idNum = parseInt(n.id, 10);
      return Number.isFinite(idNum) && idNum > m ? idNum : m;
    }, 0);

    // FIRST POLL after login (no persisted seen id): seed lastSeen to maxId, no toasts.
    if (!initialisedRef.current) {
      lastSeenIdRef.current = maxId;
      initialisedRef.current = true;
      try {
        sessionStorage.setItem(SEEN_STORAGE_KEY, String(maxId));
      } catch {
        // ignore
      }
      return;
    }

    const lastSeen = lastSeenIdRef.current ?? 0;

    // Find new + unread notifications, oldest-first so toast stack reads naturally.
    const newOnes = notifications
      .filter((n) => {
        const idNum = parseInt(n.id, 10);
        return Number.isFinite(idNum) && idNum > lastSeen && !n.readAt;
      })
      .slice()
      .reverse();

    if (newOnes.length === 0) return;

    let chimePlayed = false;
    for (const n of newOnes) {
      fireToastForNotification(n, t, pushToast, () => setIsOpen(true), {
        openChatThread: (threadId: string) => {
          selectThread(String(threadId));
          setChatOpen(true);
        },
      });

      // Play chime once per polling batch (don't spam audio).
      if (!chimePlayed && audioUnlocked) {
        try {
          const audio = new Audio('/sounds/chime.wav');
          audio.volume = 0.4;
          audio.play().catch(() => {
            // autoplay blocked — silent fallback
          });
          chimePlayed = true;
        } catch {
          // silent
        }
      }
    }

    lastSeenIdRef.current = maxId;
    try {
      sessionStorage.setItem(SEEN_STORAGE_KEY, String(maxId));
    } catch {
      // ignore
    }
  }, [notifications, audioUnlocked, t, pushToast]);

  // Close dropdown on click outside
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Visible slice in the dropdown
  const visibleNotifications = useMemo(() => notifications.slice(0, 10), [notifications]);

  const handleNotificationClick = async (notification: NotificationDto) => {
    // Toggle inline expansion for bug-resolved type (no navigation).
    if (notification.type === 'bug_report_resolved') {
      setExpandedIds((m) => ({ ...m, [notification.id]: !m[notification.id] }));
    }

    // Mark as read (best-effort)
    try {
      await notificationsApi.markAsRead(notification.id);
      // Refresh count + list so the unread badge updates immediately
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    } catch {
      // ignore
    }

    if (notification.type === 'missed_booking') {
      if (userRole === 'admin' || userRole === 'supervisor') {
        navigate('/admin/missed-bookings');
        setIsOpen(false);
      }
      // agents: stay on the dropdown, just mark-read
      return;
    }

    // bug_report_resolved: stay open so the inline detail is visible.
    if (notification.type === 'bug_report_resolved') {
      return;
    }

    if (notification.type === 'screening_flagged_by_uploader' || notification.type === 'findhelp_sla_breach') {
      if (userRole === 'admin' || userRole === 'supervisor') {
        navigate('/admin/uploader-team');
        setIsOpen(false);
      }
      return;
    }

    if (notification.type === 'your_screening_was_flagged') {
      // No deep link target yet — close dropdown and let the agent see the detail in-bell.
      setIsOpen(false);
      return;
    }

    if (notification.type === 'chat_message') {
      const p = (notification.payload || {}) as ChatMessagePayload;
      if (p.threadId) {
        selectThread(String(p.threadId));
      }
      setChatOpen(true);
      setIsOpen(false);
      return;
    }

    // F5-R6c: cambió la configuración de «Chat y notificaciones» — el admin
    // aterriza en la ventana; el resto sólo lee el aviso (no tiene la ruta).
    if (notification.type === 'notify_settings_changed') {
      if (userRole === 'admin') {
        navigate('/admin/chat-notifications');
      }
      setIsOpen(false);
      return;
    }

    setIsOpen(false);
  };

  const handleMarkAllRead = async () => {
    try {
      await notificationsApi.markAllAsRead();
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    } catch {
      // Ignore error
    }
  };

  return (
    <div ref={containerRef} className="relative">
      {/* F5-R1a — 44px de blanco mínimo; el punto de no-leídas es el GOLD
          reservado del brand (handoff F5 §1: logo · thumb del toggle · punto
          de la campana). */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          'inline-flex items-center justify-center relative h-11 w-11 rounded-[10px] text-nav-slate',
          'hover:bg-nav-soft hover:text-nav-navy transition-colors',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-nav-teal',
        )}
        aria-label={t('notifications.title')}
        title={t('notifications.title')}
      >
        <Bell className="h-4 w-4" />
        {unreadCount > 0 && (
          <span className="absolute top-2 right-2 h-2 w-2 bg-nav-gold rounded-full" aria-label={`${unreadCount} unread`} />
        )}
      </button>

      {isOpen && (
        <div
          className="absolute right-0 mt-2 w-96 max-w-[calc(100vw-2rem)] bg-white border-[1.5px] border-nav-card rounded-[14px] z-50 overflow-hidden font-body text-nav-navy"
          style={{ boxShadow: '0 24px 60px rgba(18,43,68,.14)' }}
        >
          <div className="border-b border-nav-hair px-4 py-3 flex items-center justify-between">
            <h3 className="font-head font-bold text-sm text-nav-navy">{t('notifications.title')}</h3>
            {unreadCount > 0 && (
              <button
                onClick={handleMarkAllRead}
                className="font-head text-xs font-semibold text-nav-teal hover:text-nav-tealDark transition-colors"
              >
                {t('notifications.markAllRead')}
              </button>
            )}
          </div>

          <div className="max-h-[28rem] overflow-y-auto modal-scroll">
            {visibleNotifications.length === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-nav-slate">
                {t('notifications.empty')}
              </div>
            ) : (
              <ul className="divide-y divide-nav-hair">
                {visibleNotifications.map((notif) => (
                  <NotificationRow
                    key={notif.id}
                    notif={notif}
                    expanded={!!expandedIds[notif.id]}
                    onClick={() => handleNotificationClick(notif)}
                    t={t}
                  />
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

interface NotificationRowProps {
  notif: NotificationDto;
  expanded: boolean;
  onClick: () => void;
  t: ReturnType<typeof useT>;
}

function NotificationRow({ notif, expanded, onClick, t }: NotificationRowProps): JSX.Element {
  const isBugResolved = notif.type === 'bug_report_resolved';
  const payload = (notif.payload || {}) as BugResolvedPayload & MissedBookingPayload & ChatMessagePayload;

  let title = t('notifications.toast.genericTitle');
  let subtitle: string | null = null;
  let icon: JSX.Element = <Bell className="h-4 w-4 mt-0.5 text-nav-slate shrink-0" />;

  if (notif.type === 'missed_booking') {
    title = t('notifications.type.missedBooking.title');
    const last = payload.patientLastName ?? '';
    const mrn = payload.patientMrnLast6 ?? '';
    const agent = payload.agentName ?? '';
    subtitle = `${last}${mrn ? ` (${mrn})` : ''}${agent ? ` • ${agent}` : ''}`;
    icon = <AlertCircle className="h-4 w-4 mt-0.5 text-nav-red shrink-0" />;
  } else if (notif.type === 'chat_message') {
    const cp = (notif.payload || {}) as ChatMessagePayload;
    title = t('notifications.chat.title', { name: cp.senderName ?? '' });
    subtitle = cp.preview ?? '';
    icon = <MessageCircle className="h-4 w-4 mt-0.5 text-nav-teal shrink-0" />;
  } else if (isBugResolved) {
    if (payload.status === 'wontfix') {
      title = t('notifications.bugResolved.title.wontfix');
      icon = <XCircle className="h-4 w-4 mt-0.5 text-nav-teal shrink-0" />;
    } else {
      title = t('notifications.bugResolved.title.resolved');
      icon = <CheckCircle2 className="h-4 w-4 mt-0.5 text-nav-sageMid shrink-0" />;
    }
    const orig = payload.originalMessage ?? '';
    const truncated = orig.length > 80 ? `${orig.slice(0, 80)}…` : orig;
    subtitle = t('notifications.bugResolved.subtitle', { message: truncated });
  } else if (notif.type === 'screening_flagged_by_uploader') {
    title = t('notifications.type.screeningFlaggedByUploader.title');
    const p = (notif.payload || {}) as UploaderFlagAdminPayload;
    const last = p.clientLastName ?? '';
    const mrn = p.clientCinLast6 ?? '';
    const agent = p.agentName ?? '';
    const errPreview = (p.error ?? '').slice(0, 80);
    subtitle = `${last}${mrn ? ` (${mrn})` : ''}${agent ? ` • agent ${agent}` : ''}${errPreview ? ` — ${errPreview}` : ''}`;
    icon = <AlertCircle className="h-4 w-4 mt-0.5 text-nav-red shrink-0" />;
  } else if (notif.type === 'your_screening_was_flagged') {
    title = t('notifications.type.yourScreeningWasFlagged.title');
    const p = (notif.payload || {}) as UploaderFlagAgentPayload;
    const last = p.clientLastName ?? '';
    const mrn = p.clientCinLast6 ?? '';
    const errPreview = (p.error ?? '').slice(0, 80);
    subtitle = `${last}${mrn ? ` (${mrn})` : ''}${errPreview ? ` — ${errPreview}` : ''}`;
    icon = <AlertCircle className="h-4 w-4 mt-0.5 text-nav-amber shrink-0" />;
  } else if (notif.type === 'findhelp_sla_breach') {
    title = t('notifications.type.findhelpSlaBreach.title');
    const p = (notif.payload || {}) as SlaBreachPayload;
    const last = p.clientLastName ?? '';
    const mrn = p.clientCinLast6 ?? '';
    const agent = p.agentName ?? '';
    const hours = typeof p.hoursSinceSubmit === 'number' ? ` • ${p.hoursSinceSubmit}h overdue` : '';
    subtitle = `${last}${mrn ? ` (${mrn})` : ''}${agent ? ` • ${agent}` : ''}${hours}`;
    icon = <AlertCircle className="h-4 w-4 mt-0.5 text-nav-red shrink-0" />;
  } else if (notif.type === 'findhelp_sla_breach_digest') {
    // F5-R9u: el tipo VIVO del cron sla-monitor no tenía renderer y caía al
    // título genérico (el legacy findhelp_sla_breach ya no tiene emisor).
    // Sin PHI: el resumen solo dice cuántos.
    const p = (notif.payload || {}) as { count?: number };
    title = t('notifications.type.findhelpSlaDigest.title');
    subtitle = t('notifications.type.findhelpSlaDigest.body', {
      n: String(typeof p.count === 'number' ? p.count : '—'),
    });
    icon = <AlertCircle className="h-4 w-4 mt-0.5 text-nav-red shrink-0" />;
  } else if (notif.type === 'nav_vigia_alert' || notif.type === 'nav_rebalance_ceded') {
    // E-35: el vigía de navegación (a los admin) y «se movieron N casos sin
    // contactar de tu lista» (a la navegadora que cedió). El texto lo arma el
    // servidor, sin PHI; sin esta rama la fila decía «Notificación» y nada más.
    const p = (notif.payload || {}) as { message?: string };
    title = t(notif.type === 'nav_vigia_alert'
      ? 'notifications.type.navVigiaAlert.title'
      : 'notifications.type.navRebalanceCeded.title');
    subtitle = p.message ?? null;
    icon = notif.type === 'nav_vigia_alert'
      ? <AlertCircle className="h-4 w-4 mt-0.5 text-nav-amber shrink-0" />
      : <Bell className="h-4 w-4 mt-0.5 text-nav-teal shrink-0" />;
  } else if (notif.type === 'notify_settings_changed') {
    // F5-R6c: aviso del propio grupo «Chat y notificaciones» (sin PHI —
    // el payload sólo trae qué claves cambiaron y quién).
    const p = (notif.payload || {}) as { changedByName?: string };
    title = t('f5r6c.bell.title');
    subtitle = t('f5r6c.bell.body', { name: p.changedByName ?? '—' });
    icon = <Bell className="h-4 w-4 mt-0.5 text-nav-teal shrink-0" />;
  }

  return (
    <li
      onClick={onClick}
      className={cn(
        'px-4 py-3 cursor-pointer transition-colors',
        notif.readAt ? 'bg-white hover:bg-nav-softer' : 'bg-nav-tealTint/40 hover:bg-nav-tealTint',
      )}
    >
      <div className="flex items-start gap-3">
        {icon}
        <div className="flex-1 min-w-0">
          <p className="font-head font-semibold text-sm text-nav-navy">{title}</p>
          {subtitle && (
            <p className="text-xs text-nav-slate mt-1 line-clamp-2">{subtitle}</p>
          )}
          <p className="text-xs text-nav-slate mt-1">{formatRelativeTime(new Date(notif.createdAt))}</p>
        </div>
        <div className="flex flex-col items-end gap-1 shrink-0">
          {!notif.readAt && <div className="h-2 w-2 bg-nav-teal rounded-full mt-1.5" />}
          {isBugResolved && (
            <span className="text-nav-faint" aria-label={expanded ? t('notifications.bugResolved.expandHide') : t('notifications.bugResolved.expandShow')}>
              {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            </span>
          )}
        </div>
      </div>

      {isBugResolved && expanded && (
        <BugResolvedDetail payload={payload} t={t} />
      )}
    </li>
  );
}

function BugResolvedDetail({ payload, t }: { payload: BugResolvedPayload; t: ReturnType<typeof useT> }): JSX.Element {
  const sevKey = payload.originalSeverity ?? 'medium';
  const sevTone = SEVERITY_TONE[sevKey] ?? SEVERITY_TONE.medium!;
  const statusKey = payload.status ?? 'resolved';
  const statusTone = STATUS_TONE[statusKey] ?? STATUS_TONE.resolved!;

  const sevLabelKey = `notifications.bugResolved.severity.${sevKey}` as StringKey;
  const statusLabelKey = `notifications.bugResolved.status.${statusKey}` as StringKey;

  return (
    <div
      className="mt-3 ml-7 rounded-[10px] bg-nav-soft border border-nav-card px-3 py-3 space-y-2 text-xs"
      onClick={(e) => e.stopPropagation()}
    >
      <div>
        <p className="text-nav-navy whitespace-pre-wrap break-words">{payload.originalMessage}</p>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-nav-slate">{t('notifications.bugResolved.severityLabel')}:</span>
        <span className={cn('inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium', sevTone.bg, sevTone.text)}>
          {t(sevLabelKey)}
        </span>
        <span className="text-nav-slate ml-2">{t('notifications.bugResolved.statusLabel')}:</span>
        <span className={cn('inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium', statusTone.bg, statusTone.text)}>
          {t(statusLabelKey)}
        </span>
      </div>

      <div>
        <p className="text-nav-slate mb-0.5">{t('notifications.bugResolved.resolutionLabel')}:</p>
        {payload.resolutionNote ? (
          <p className="text-nav-navy whitespace-pre-wrap break-words">{payload.resolutionNote}</p>
        ) : (
          <p className="text-nav-slate italic">{t('notifications.bugResolved.noNote')}</p>
        )}
      </div>

      {payload.resolvedByName && (
        <div className="text-nav-slate">
          {t('notifications.bugResolved.resolvedByLabel')}: <span className="text-nav-navy">{payload.resolvedByName}</span>
          {payload.resolvedAt && <span className="text-nav-slate"> • {formatRelativeTime(new Date(payload.resolvedAt))}</span>}
        </div>
      )}
    </div>
  );
}

// Cluster T-5: notification types that should NEVER pop a toast (still
// surface in the bell badge + bell dropdown). User anti-saturation policy
// — only high-priority comms (chat messages, missed bookings, bug-report
// responses) and SLA breaches deserve interruption. System-level outcome
// notifications get a quiet bell badge instead.
const TOAST_SUPPRESSED_TYPES = new Set<string>([
  'your_screening_was_flagged', // 'screening rejected' from agent POV
  'household_promoted',         // future-proof: not currently firing toasts
]);

// Toast firing logic — exported as a side-effect-free helper for tests if ever needed.
function fireToastForNotification(
  n: NotificationDto,
  t: ReturnType<typeof useT>,
  pushToast: (toast: { variant: 'info' | 'success' | 'warning' | 'error' | 'vip'; title: string; description?: string; durationMs?: number; onClick?: () => void }) => string,
  openBell: () => void,
  hooks: { openChatThread?: (threadId: string) => void } = {},
): void {
  // Cluster T-5: anti-saturation gate. Suppressed types still hit the bell
  // (the badge count + dropdown list are independent of this toast pipeline).
  if (TOAST_SUPPRESSED_TYPES.has(n.type)) return;

  const payload = (n.payload || {}) as BugResolvedPayload & MissedBookingPayload & ChatMessagePayload;

  let variant: 'info' | 'success' | 'warning' | 'error' | 'vip' = 'info';
  let title = t('notifications.toast.genericTitle');
  let description: string | undefined;
  let onClick: (() => void) | undefined;

  if (n.type === 'missed_booking') {
    variant = 'warning';
    const patient = payload.patientLastName ?? '';
    title = t('notifications.toast.missedBooking.title', { patient });
    description = payload.agentName ?? undefined;
  } else if (n.type === 'bug_report_resolved') {
    if (payload.status === 'wontfix') {
      variant = 'info';
      title = t('notifications.toast.bugResolved.wontfix');
    } else {
      variant = 'success';
      title = t('notifications.toast.bugResolved.resolved');
    }
    const orig = payload.originalMessage ?? '';
    description = orig.length > 100 ? `${orig.slice(0, 100)}…` : orig;
  } else if (n.type === 'chat_message') {
    variant = 'info';
    title = t('notifications.toast.chatMessage.title');
    const sender = payload.senderName ?? '';
    const preview = payload.preview ?? '';
    const previewTrim = preview.length > 100 ? `${preview.slice(0, 100)}…` : preview;
    description = sender ? `${sender}: ${previewTrim}` : previewTrim;
    // Cluster T-5: clicking the chat toast deep-links into the thread.
    if (payload.threadId && hooks.openChatThread) {
      const tid = String(payload.threadId);
      const opener = hooks.openChatThread;
      onClick = () => opener(tid);
    }
  } else if (n.type === 'screening_flagged_by_uploader') {
    variant = 'warning';
    title = t('notifications.toast.screeningFlaggedByUploader.title');
    const p = (n.payload || {}) as UploaderFlagAdminPayload;
    description = `${p.clientLastName ?? ''}${p.error ? ` — ${(p.error ?? '').slice(0, 80)}` : ''}`.trim() || undefined;
  } else if (n.type === 'your_screening_was_flagged') {
    variant = 'warning';
    title = t('notifications.toast.yourScreeningWasFlagged.title');
    const p = (n.payload || {}) as UploaderFlagAgentPayload;
    description = `${p.clientLastName ?? ''}${p.error ? ` — ${(p.error ?? '').slice(0, 80)}` : ''}`.trim() || undefined;
  } else if (n.type === 'findhelp_sla_breach') {
    variant = 'warning';
    title = t('notifications.toast.findhelpSlaBreach.title');
    const p = (n.payload || {}) as SlaBreachPayload;
    description = `${p.clientLastName ?? ''}${p.agentName ? ` • ${p.agentName}` : ''}${typeof p.hoursSinceSubmit === 'number' ? ` • ${p.hoursSinceSubmit}h` : ''}`.trim() || undefined;
  } else if (n.type === 'nav_vigia_alert' || n.type === 'nav_rebalance_ceded') {
    // E-35: mismo texto que la fila de la campana (armado por el servidor).
    variant = n.type === 'nav_vigia_alert' ? 'warning' : 'info';
    title = t(n.type === 'nav_vigia_alert'
      ? 'notifications.type.navVigiaAlert.title'
      : 'notifications.type.navRebalanceCeded.title');
    description = ((n.payload || {}) as { message?: string }).message ?? undefined;
  } else if (n.type === 'notify_settings_changed') {
    // F5-R6c: cambió qué avisa el sistema — informativo, sin PHI.
    variant = 'info';
    title = t('f5r6c.bell.title');
    const p = (n.payload || {}) as { changedByName?: string };
    description = t('f5r6c.bell.body', { name: p.changedByName ?? '—' });
  } else {
    // Unknown type — generic
    variant = 'info';
    title = t('notifications.toast.genericTitle');
  }

  pushToast({
    variant,
    title,
    description,
    durationMs: 6000,
    onClick,
  });

  // Suppress unused-var warning for openBell — left in signature for a future "click toast → open bell" hook.
  void openBell;
}

function formatRelativeTime(date: Date): string {
  const now = new Date();
  const diff = now.getTime() - date.getTime();
  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (seconds < 60) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 7) return `${days}d ago`;

  return date.toLocaleDateString();
}
